/**
 * The three products, as a person would describe them.
 *
 * COPY LIVES HERE, not in the launcher, because the same words have to appear
 * in the launcher, in a locked state, and eventually on a marketing page — and
 * three hand-written variants of "what Beats is" is how a product ends up
 * describing itself differently depending on where you found it.
 *
 * The KEYS come from the server (`GET /billing/me` returns `available`), so this
 * is presentation only. A product added to `jcseo/types.py:Product` without an
 * entry here still works; it just reads as its raw key until someone writes the
 * sentence, which is the right failure — visible, harmless, and obviously
 * unfinished.
 *
 * Dependency-free so CRA's jest can test it.
 */
import type { Product } from '../types';
import { SHELLS, type Shell } from './shell';

export interface ProductInfo {
  key: Product;
  /** The wordmark, and what the shell calls itself. */
  name: string;
  /** The headline. What you would say to someone who asked what it is for. */
  headline: string;
  /** A short paragraph under the headline. */
  tagline: string;
  /** The selling points, as [label, sentence] pairs, for a marketing page. */
  points: [string, string][];
  /** The name inside a sentence ("Applies to Publish"); `name` is the brand
   *  where it stands alone (the launcher, the account menu, billing). */
  short: string;
  /** What appears in the nav once you hold it, in the nav's own words. */
  unlocks: string[];
  /** The shell it is served from. */
  shell: Shell;
}

export const PRODUCT_INFO: Record<Product, ProductInfo> = {
  BEATS: {
    key: 'BEATS',
    name: 'Lumnian News Beats',
    short: 'News Beats',
    headline: 'Know what matters. Skip the noise.',
    tagline:
      'News Beats continuously monitors the topics that matter to you and turns the latest developments into concise, useful briefings. It filters duplicates, follows stories over time, and tells you what’s actually new - so you can stay informed without living in the news.',
    points: [
      ['Your topics, your schedule', 'daily, weekly, or whenever you choose.'],
      ['Less noise', 'duplicate stories and recycled coverage are filtered out.'],
      ['Know what changed', 'ongoing stories are updated instead of repeated.'],
      ['Your brand', 'send or publish briefings under your own name.'],
    ],
    unlocks: ['News Beats'],
    shell: 'beats',
  },
  PUBLISH: {
    key: 'PUBLISH',
    name: 'Lumnian Publish',
    short: 'Publish',
    headline: 'Turn search demand into pages worth publishing.',
    tagline:
      'Publish finds what people are searching for, researches the subject, and turns that research into authoritative, search-focused content. You stay in control: review every page, make any changes you want, and approve it before it goes live on your site.',
    points: [
      ['Find the opportunity', 'discover subjects with growing search demand.'],
      ['Research before writing', 'build pages from search results and reliable sources.'],
      ['You stay in control', 'review, edit, and approve every page before publishing.'],
      ['Publish and connect', 'deploy directly to your site and build links between related pages.'],
    ],
    unlocks: ['Trends', 'Subjects', 'Pages', 'Sites & deploys', 'Review', 'Link Builder'],
    shell: 'publish',
  },
  BOARDROOM: {
    key: 'BOARDROOM',
    name: 'Lumnian Recon',
    short: 'Recon',
    headline: 'Understand a market before you bet on it.',
    tagline:
      'Turn a business idea, market, or competitor into a research project. Recon deploys a team of AI researchers to investigate competitors, products, pricing, customer pain, search demand, and more - then brings everything together into one evidence-backed picture of the market.',
    points: [
      ['Map the market', 'competitors, products, features, pricing, and positioning.'],
      ['Find the gaps', 'uncover unmet customer needs and opportunities others are missing.'],
      ['Follow the evidence', 'every finding is tied back to its source.'],
      ['Keep watching', 'track competitors, products, and markets for meaningful changes over time.'],
    ],
    unlocks: ['Recons', 'Watch'],
    shell: 'boardroom',
  },
};

/** In catalog order, so the launcher does not depend on object key order. */
export const PRODUCT_ORDER: Product[] = ['BOARDROOM', 'BEATS', 'PUBLISH'];

/** The host a product's shell is served from, for the current domain.
 *
 *  Derived from where you ARE rather than hardcoded, so this works unchanged on
 *  lumnian.com, lumnian.test and anything else — the same property that makes
 *  `shellFromHost` domain-agnostic. */
export function shellUrl(shell: Shell, hostname: string, origin: string): string {
  const parts = hostname.split('.');
  if (parts.length < 2) return origin; // localhost or an IP: nowhere else to go
  const domain = parts.slice(1).join('.');
  // `URL` rather than string surgery: it carries the scheme and the port across
  // for free. Hand-rolling that got the port wrong, because every origin
  // contains a colon in `://`.
  const url = new URL(origin);
  url.hostname = `${SHELLS[shell].host}.${domain}`;
  return url.origin;
}
